import { useCallback, useMemo, useState } from 'react';
import { API_STATUS } from '../../utilities/constants';

const useApiClient = (ApiClientClass, apiVer) => {
  const [apiStatus, setApiStatus] = useState(API_STATUS.IDLE);

  const isLoading = apiStatus === API_STATUS.LOADING;
  const isSucceeded = apiStatus === API_STATUS.SUCCEEDED;
  const isError = apiStatus === API_STATUS.ERROR;
  const isFinished = isSucceeded || isError;

  const resetApiStatus = useCallback(() => {
    if (apiStatus !== API_STATUS.IDLE) {
      setApiStatus(API_STATUS.IDLE);
    }
  }, [apiStatus, setApiStatus]);

  const functionsFromClass = useMemo(() => {
    const callApi = async ({ apiMethod, apiArgs = [], callbacks = {} }) => {
      const apiArgsRequiredLength = apiMethod.length;
      if (apiArgs.length !== apiArgsRequiredLength) {
        throw new Error(
          `Insufficient arguments provided for API method. Expected at least ${apiArgsRequiredLength}, received ${apiArgs.length}.`
        );
      }

      const { onStart = () => {}, onFinished = () => {}, onSuccess = () => {}, onFailure = () => {} } = callbacks;

      const apiClient = new ApiClientClass(apiVer);

      onStart();
      setApiStatus(API_STATUS.LOADING);

      try {
        const result = await apiMethod.apply(apiClient, apiArgs);
        const data = result?.data ?? result;

        onSuccess(data);
        setApiStatus(API_STATUS.SUCCEEDED);
      } catch (error) {
        onFailure(error);
        setApiStatus(API_STATUS.ERROR);
      } finally {
        onFinished();
      }
    };

    return Object.getOwnPropertyNames(ApiClientClass.prototype).reduce((methodCallers, key) => {
      const apiMethod = ApiClientClass.prototype[key];
      if (key === 'constructor' || typeof apiMethod !== 'function') return methodCallers;

      methodCallers[key] = argsCallbacksAndVersion =>
        callApi({
          ...argsCallbacksAndVersion,
          apiMethod
        });
      return methodCallers;
    }, {});
  }, [ApiClientClass, apiVer, setApiStatus]);

  /**
   * Current 3 equivalent ways to read the API fetch Status:
   * 1. Through each's component calculation of states from the onSuccess or onFailure of the Array of functions from the ApiClientClass
   * 2. Through the updated apiStatus state
   *          (currently not used anywhere)
   * 3. Through the isLoading, isError, isFinished, isSucceeded precalculated flags
   *          (the intention of a future PR, would be to remove them and instead
   *          to favor the usage of apiStatus state directly on the components)
   */

  return {
    ...functionsFromClass,
    apiStatus,
    resetApiStatus,
    isLoading,
    isError,
    isFinished,
    isSucceeded
  };
};

export default useApiClient;
